import React, { useRef, useState, useEffect, useCallback } from 'react';
import { connect } from 'react-redux';

import { Modal } from '../../custom-essentials';
import LearningPlanCompleteBodyFooter from './LearningPlanCompleteBF';
import { checkResponse } from '../../form';

import {
    fetchLearningPlansId,
    fetchStudentsUserIds,
    updateLearningPlanComplete
} from '../../../actions';

function LearningPlanCompleteModal(props){
    const mounted = useRef(false);
    useEffect(() => {
        mounted.current = true;
        return () => (mounted.current = false);
    });

    const [showModal, setShowModal] = useState(true);
    const [loaded, setLoaded] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [attemptingClose, setAttemptingClose] = useState(false);
    
    const [learningPlan, setLearningPlan] = useState({});

    const { selectedLearningPlan, selectedStudent, onSubmitCallback,
        fetchLearningPlansId, fetchStudentsUserIds, updateLearningPlanComplete } = props;

    useEffect(() => {
        async function init(){           
            const selectedLearningPlanIdRaw =
                selectedLearningPlan?.id ?? selectedLearningPlan?.value ?? selectedLearningPlan?.obj?.id;
            const selectedLearningPlanId = parseInt(selectedLearningPlanIdRaw);

            if(!selectedLearningPlanId || Number.isNaN(selectedLearningPlanId)){
                if(mounted.current){
                    setLearningPlan({});
                    setLoaded(true);
                }
                return;
            }

            const studentsRes = await fetchStudentsUserIds({ userIds: [selectedStudent.value] });
            const newStudent = studentsRes.data?.[0] || {};
            const newStudentName = newStudent.user_id ?
                `${newStudent.first_name} ${newStudent.last_name}` : `Unknown student (UID: ${selectedStudent.value})`;

            const learningPlansRes = await fetchLearningPlansId({ id: selectedLearningPlanId });
            const newLearningPlan = learningPlansRes.data?.learningPlans?.[0] || {};
            newLearningPlan.studentName = newStudentName;

            if(mounted.current){
                setLearningPlan(newLearningPlan);
            }
            
            if(mounted.current){
                setLoaded(true);
            }
        };
        init();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleClose = useCallback((changes, force = false) => {
        if(!attemptingClose && !force){
            setAttemptingClose(true);
            return;
        }
        if(changes !== true) changes = false;
        async function close(){
            await onSubmitCallback(changes);
            if(mounted.current) setShowModal(false);
        }
        close();
    }, [onSubmitCallback, attemptingClose, setAttemptingClose]);

    const isCurrentlyActive = parseInt(learningPlan?.active) === 1;
    
    const handleSubmit = useCallback((values, actions) => {
        async function submit(){
            const { setStatus, setSubmitting } = actions;
            
            // Only require confirmation when marking complete (active -> inactive), not when reactivating
            if(isCurrentlyActive && values.confirmText !== 'Confirm'){
                setSubmitting(false);
                setStatus('Confirmation text is required.')
                return;
            }

            if(!learningPlan?.id){
                setSubmitting(false);
                setStatus('No learning plan is selected. Please close this modal and select a learning plan first.');
                return;
            }
            
            // Ensure learningPlan has all required properties
            const learningPlanPayload = {
                id: learningPlan.id,
                student: learningPlan.student,
                name: learningPlan.name || '',
                studentName: learningPlan.studentName || ''
            };
            
            console.log('[LearningPlanCompleteModal] Submitting with learningPlan:', learningPlanPayload);
            console.log('[LearningPlanCompleteModal] isCurrentlyActive:', isCurrentlyActive);
            
            let response = { status: 999 };
            
            response = await updateLearningPlanComplete({
                learningPlan: learningPlanPayload,
                additionalMessage: values.additionalMessage || ''
            })
            
            console.log('[LearningPlanCompleteModal] Response:', response);

            const responseValid = checkResponse(response, mounted, setStatus);
            if(!responseValid && mounted.current){
                setSubmitting(false);
                return;
            }
            
            if(mounted.current) setSubmitted(true);
            setTimeout(() => handleClose(true, true), 1000);
        }
        submit();
    }, [handleClose, updateLearningPlanComplete, learningPlan, isCurrentlyActive]);

    const modalTitle = isCurrentlyActive ? 'Learning Plan Complete Form' : 'Learning Plan Reactivate Form';
    
    return (
        <Modal className="w-2/3" show={showModal} onHide={handleClose}>
            <Modal.Header>
                <h2>{modalTitle}</h2>
            </Modal.Header>
            <LearningPlanCompleteBodyFooter
                learningPlan={learningPlan}
                loaded={loaded}
                submitted={submitted}
                handleClose={handleClose}
                attemptingClose={attemptingClose}
                setAttemptingClose={setAttemptingClose}
                handleSubmit={handleSubmit}
                isCurrentlyActive={isCurrentlyActive}
            />
        </Modal>
    );
}

export default connect(null, {
    fetchLearningPlansId,
    fetchStudentsUserIds,
    updateLearningPlanComplete
})(LearningPlanCompleteModal);